import _ from 'lodash';
import { t } from '@lingui/macro';

import { convertQosSelection } from './VoipAgentDetailHelper';
import { AGENT_STATUS_FIELD, AGENT_STATUS_RUNNING } from '../agents/AgentConstants';

const NO_MEDIA_SECURITY = 0;
const OPTIONAL_MEDIA_SECURITY = 1;
const MANDATORY_MEDIA_SECURITY = 2;
const VVLAN_MODE_NONE = 'N';
const QUICK_TEST_STATUS_INITIALIZING = 'I';
const QUICK_TEST_STATUS = {
    INITIALIZING: 'Initializing',
    COMPLETE: 'Complete',
    CANCELED: 'Canceled',
    ERROR: 'Error',
};
const VERIFY_POLL_INTERVAL_MS = 2000;
const VERIFY_TIMEOUT_SECONDS = 215;
const VERIFY_POLL_MAX_COUNT = Math.ceil((VERIFY_TIMEOUT_SECONDS * 1000) / VERIFY_POLL_INTERVAL_MS) + 10;

const sleep = (ms) => new Promise((resolve) => setTimeout(resolve, ms));

const getDialToneResult = (quickTest = {}) => {
    const agentResult = _.first(_.get(quickTest, 'agentResults', []));
    return _.first(_.get(agentResult, 'results', []));
};

const isDialToneSuccess = (quickTest = {}) => {
    return (
        _.get(quickTest, 'status') === QUICK_TEST_STATUS.COMPLETE &&
        !!_.get(getDialToneResult(quickTest), 'data.is_pass')
    );
};

const verifyHasResults = (quickTest = {}) => {
    return _.get(quickTest, 'status') !== QUICK_TEST_STATUS.INITIALIZING;
};

export const buildVerifyErrorResult = (error) => {
    const validationErrors = _.get(error, 'details.errors', _.get(error, 'validations'));
    if (Array.isArray(validationErrors) && validationErrors.length > 0) {
        const validationMessages = validationErrors
            .map((validationError) => _.get(validationError, 'message'))
            .filter(Boolean)
            .join('\n');

        return {
            status: t`Error`,
            statusMsg: t`The VoIP configuration doesn't seem to be valid. The test returned a failure.`,
            statusHtml: validationMessages,
        };
    }

    const dataMessage = _.get(error, 'data.message');
    if (dataMessage === 'vtn_agents_already_in_call') {
        return {
            status: t`Error`,
            statusMsg: t`One or more of the selected nPoint is already in a call.`,
            statusHtml: '',
        };
    }

    if (dataMessage === 'voip_wireless_adapter_not_enabled') {
        return {
            status: t`Error`,
            statusMsg: t`Wireless adapter for this nPoint is currently disabled or running Wi-Fi Connection Tests.`,
            statusHtml: '',
        };
    }

    return {
        status: t`Error`,
        statusMsg: t`There was an error trying to verify this test.  If this problem persists, please contact support.`,
        statusHtml: '',
    };
};

export const buildVoipDialToneVerifyRequestBody = ({ agent, selectedCallManager, qosSelection }) => {
    if (!selectedCallManager?._id) {
        throw new Error(t`Please select a Call Manager before running verify.`);
    }

    const verifyAgent = _.cloneDeep(agent || {});
    verifyAgent.voipConfiguration = verifyAgent.voipConfiguration || {};

    convertQosSelection(verifyAgent.voipConfiguration, qosSelection);

    const { voipConfiguration } = verifyAgent;
    const verifyTest = {
        name: 'Verify nPoint Dial Tone',
        quickTestType: 'VoipDial',
        verify: true,
        adapterType: _.get(voipConfiguration, 'adapterType', 'default'),
        testConfig: {
            callManagerId: selectedCallManager._id,
            ip_version: _.get(voipConfiguration, 'ipVersion'),
            sip_user: _.get(voipConfiguration, 'sipUserId'),
            sip_url: {},
            auth_name: { value: _.get(voipConfiguration, 'encryptedAuthorizationName.value') },
            is_quick_test: true,
            quick_test: true,
            enableSecurity: !!selectedCallManager.enableSecurity,
            verifyCMCertificate: selectedCallManager.verifyCMCertificate,
        },
        agents: { all: false, ids: [verifyAgent._id] },
        agentResults: [
            {
                agent: verifyAgent._id,
                agentGuid: verifyAgent.guid,
                events: [],
                results: [],
                status: QUICK_TEST_STATUS_INITIALIZING,
            },
        ],
    };

    const encryptedPassword = _.get(voipConfiguration, 'encryptedPassword.value');
    if (encryptedPassword) {
        verifyTest.testConfig.sip_password = { value: encryptedPassword };
    }

    if (verifyTest.testConfig.enableSecurity) {
        verifyTest.testConfig.srtpMode = voipConfiguration.enableUnsecureCall
            ? OPTIONAL_MEDIA_SECURITY
            : MANDATORY_MEDIA_SECURITY;
    } else {
        verifyTest.testConfig.srtpMode = NO_MEDIA_SECURITY;
    }

    if (_.get(voipConfiguration, 'vvlan')) {
        verifyTest.testConfig.vvlan_mode = _.get(voipConfiguration, 'vvlan.mode');
        verifyTest.testConfig.vvlan_id = _.get(voipConfiguration, 'vvlan.vlanId', '');
    } else {
        verifyTest.testConfig.vvlan_mode = VVLAN_MODE_NONE;
    }

    if (_.get(voipConfiguration, 'qos')) {
        verifyTest.testConfig.l2Priority = _.get(voipConfiguration, 'qos.l2Priority');
        verifyTest.testConfig.l3Priority = _.get(voipConfiguration, 'qos.l3Priority');
    }

    return verifyTest;
};

const pollVerifyQuickTest = async ({ verifyId, dataService, setVerifyResult, pollCount = 0 }) => {
    if (pollCount > VERIFY_POLL_MAX_COUNT) {
        setVerifyResult({
            status: t`Failure`,
            statusMsg: t`Tests have not completed before timeout.  If this problem persists, please contact support.`,
            statusHtml: '',
        });
        return;
    }

    const latest = await dataService.get(`/ipm/v1/admin/quicktests/${verifyId}`);

    if (isDialToneSuccess(latest)) {
        setVerifyResult({
            status: t`Success`,
            statusMsg: t`The test executed successfully`,
            statusHtml: '',
        });
        return;
    }

    const latestStatus = _.get(latest, 'status');
    const stillRunning = ![QUICK_TEST_STATUS.COMPLETE, QUICK_TEST_STATUS.CANCELED, QUICK_TEST_STATUS.ERROR].includes(
        latestStatus
    );

    if (!stillRunning) {
        const failureDetails = _.get(getDialToneResult(latest), 'data.signalling_collection');
        setVerifyResult({
            status: t`Failure`,
            statusMsg: t`The VoIP configuration doesn't seem to be valid. The test returned a failure.`,
            statusHtml: failureDetails ? JSON.stringify(failureDetails, null, 2) : '',
        });
        return;
    }

    if (verifyHasResults(latest)) {
        setVerifyResult({
            status: t`Pending`,
            statusMsg: t`Verify is still processing...`,
            statusHtml: '',
        });
    }

    await sleep(VERIFY_POLL_INTERVAL_MS);
    return pollVerifyQuickTest({ verifyId, dataService, setVerifyResult, pollCount: pollCount + 1 });
};

export const runVoipDialToneVerify = async ({
    dataService,
    trigger,
    getValues,
    formData,
    selectedCallManager,
    qosSelection,
    userName,
    userId,
    setShowVerify,
    setVerifyLoading,
    setVerifyResult,
}) => {
    const isFormValid = await trigger();
    if (!isFormValid) {
        return;
    }
    if (_.get(formData, AGENT_STATUS_FIELD) !== AGENT_STATUS_RUNNING) {
        setShowVerify(true);
        setVerifyLoading(false);
        setVerifyResult({
            status: t`Failure`,
            statusMsg: t`This nPoint is not available to verify this VoIP configuration. The nPoint must be running to verify the VoIP configuration.`,
            statusHtml: '',
        });
        return;
    }

    setShowVerify(true);
    setVerifyLoading(true);
    setVerifyResult({
        status: t`Processing`,
        statusMsg: t`Configuring nPoint to verify configuration...`,
        statusHtml: '',
    });

    try {
        const verifyAgent = _.defaultsDeep(_.cloneDeep(getValues()), _.cloneDeep(formData || {}));
        const verifyBody = buildVoipDialToneVerifyRequestBody({
            agent: verifyAgent,
            selectedCallManager,
            qosSelection,
        });
        verifyBody.createdBy = userName;
        verifyBody.createdById = userId;
        verifyBody._id = null;

        const response = await dataService.post('/ipm/v1/admin/quicktests', verifyBody);
        const verifyId = _.get(response, '_id');

        if (!verifyId) {
            setVerifyResult({
                status: _.get(response, 'status', t`Success`),
                statusMsg: _.get(response, 'statusMsg', _.get(response, 'message', t`Verify completed.`)),
                statusHtml: _.get(response, 'statusHtml', ''),
            });
            return;
        }

        await pollVerifyQuickTest({
            verifyId,
            dataService,
            setVerifyResult,
        });
    } catch (error) {
        setVerifyResult(buildVerifyErrorResult(error));
    } finally {
        setVerifyLoading(false);
    }
};
